import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const mainLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shopping Guide', to: '/oopbuy-spreadsheet-guide' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70" style={{ height: '72px' }}>
        <div className="h-full px-4 md:px-8 flex items-center justify-between max-w-[1440px] mx-auto">
          {/* Left: Brand + Nav Links */}
          <div className="flex items-center gap-6 md:gap-8">
            <Link to="/" className="flex flex-col leading-tight flex-shrink-0">
              <span className="text-sm font-semibold tracking-widest text-foreground-800 font-label uppercase">
                OOPBuy
              </span>
              <span className="text-[10px] tracking-[0.3em] text-foreground-500 font-label uppercase">
                Spreadsheets
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-1">
              {mainLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.to
                      ? 'bg-accent-100 text-accent-800'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right: Category Dropdown + CTAs (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 transition-colors text-sm font-medium text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                <span>Categories</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-base"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-base"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-2xl border border-background-200/70 shadow-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-5 py-3 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors border-b border-background-100 last:border-b-0 whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-200 hover:bg-background-100 transition-colors text-sm font-medium text-foreground-800 whitespace-nowrap cursor-pointer"
            >
              OOPBuy Store
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 transition-colors text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Overlay Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-[72px] left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="p-5 space-y-2">
              {/* Nav Links */}
              {mainLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-5 py-3.5 rounded-xl text-base font-semibold transition-colors ${
                    location.pathname === link.to
                      ? 'bg-accent-100 text-accent-800'
                      : 'text-foreground-800 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Divider */}
              <div className="py-2">
                <p className="px-5 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-widest">
                  Product Categories
                </p>
              </div>

              {/* Categories */}
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 rounded-xl text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                    <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                  </div>
                  {cat.name}
                </a>
              ))}

              {/* Divider */}
              <div className="py-2">
                <p className="px-5 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-widest">
                  Quick Links
                </p>
              </div>

              {/* CTA Buttons */}
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mx-5 px-5 py-3.5 rounded-full bg-background-100 border border-background-200 text-sm font-semibold text-foreground-800 cursor-pointer"
              >
                OOPBuy Spreadsheet
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mx-5 px-5 py-3.5 rounded-full bg-primary-500 text-sm font-semibold text-background-50 cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}